/**
 * What a suspended agency sees where a control used to be - D-14 Item 2,
 * built at Phase B from WAIT-A **P-4**.
 *
 * **The server was always the guard and still is.** `requireAgencyState`
 * refuses checkout, order placement and renewal with a 403, and the Phase A
 * test run proved it does. What was missing is that nothing on the screen said
 * so: the buttons were all still there, so the only way to discover the
 * account could not spend was to fill a form in and be refused by it.
 *
 * That is worse than it sounds, because a refusal arriving after the work is
 * the shape that makes a customer think the portal is broken - and a customer
 * who thinks the portal is broken opens a support ticket or a chargeback rather
 * than reading a banner they never triggered. `AppShell`'s `SuspendedBanner`
 * doc comment has claimed since Phase A that a suspended agency is "looking at
 * their own orders and sites with the buy and order controls gone". **It was
 * describing an intention nobody had implemented.** This is that sentence
 * becoming true.
 *
 * **Never a substitute for the server check.** Everything here is a hint that
 * saves a wasted form. A browser with an old bundle, a stale session, or the
 * developer console open still meets the 403.
 */
import { useSession } from '../session/SessionProvider.js';

import { Alert } from './ui.js';

/**
 * Can this agency commit anything that costs credits?
 *
 * `false` for a suspended agency and true for everyone else, **including an
 * operator with no agency at all**: they never reach these pages, and a helper
 * that answered "no" for them would hide controls on a screen nobody was
 * complaining about.
 *
 * `invited` and `closed` are deliberately not handled. Neither can hold a
 * session, so neither can render a page that calls this.
 */
export function useAgencySuspended(): boolean {
  const { session } = useSession();
  return session?.agency?.status === 'suspended';
}

/**
 * What the agency is not able to do, as the verb phrase completing
 * "you cannot ___ while your account is suspended".
 */
export type SuspendedAction =
  | 'buy credits'
  | 'order a build'
  | 'renew a site'
  // WAIT-B round 2. Cancelling releases credits back onto an account whose
  // credits are about to be valued, so `requireAgencyState('active')` refuses
  // it and the operator cancels open orders from the offboarding panel instead.
  | 'cancel an order'
  // Creating one is preparing to order. Editing an existing one is not, and
  // the server lets a suspended account do it - see `ServerGuard` below.
  | 'add a project';

/**
 * One plain line, in place of the control.
 *
 * Deliberately **does not repeat why the account is suspended**. The operator's
 * own words are on the banner at the top of every page (`SuspendedBanner`), and
 * saying them twice in different words is how two sentences end up disagreeing.
 * This says what cannot be done here; the banner says why.
 */
export function SuspendedNotice({ action }: { action: SuspendedAction }): React.JSX.Element {
  return (
    <Alert tone="warning">
      You cannot {action} while your account is suspended.
    </Alert>
  );
}

/* --------------------------------------------- view as agency (Item 7.2) --- */

/**
 * Is this page being looked at by an operator, read-only? - D-14 Item 7.2.
 *
 * **The server is the guard** (`refuseWhenImpersonating`), and it was there
 * from Phase B. What was missing is that nothing on screen said so, so an
 * operator could fill in a whole order form and be refused at the end of it -
 * WAIT-B defect 3, and exactly the failure P-4 fixed for suspension.
 */
export function useViewingAsAgency(): boolean {
  const { session } = useSession();
  return Boolean(session?.viewingAs);
}

/**
 * One plain line, beside a control that will not write.
 *
 * **Says who is looking, not only that somebody is.** The operator already
 * knows; the sentence exists so that a screenshot, a shared screen or a
 * half-remembered tab is not mistaken for the customer's own view.
 */
export function ViewOnlyNotice(): React.JSX.Element {
  return (
    <Alert tone="info">
      You are viewing this account read-only. Nothing on this page can be changed from here.
    </Alert>
  );
}

/**
 * **Which of the server's two refusals the route behind this control carries.**
 * Added at WAIT-B round 2, when the hook reached four pages where the two
 * refusals are not the same answer.
 *
 * The server guards a customer write with one middleware or two:
 *
 * - `'active-only'` - the route carries `requireAgencyState('active')` as well
 *   as `refuseWhenImpersonating`, so a suspended account is refused too. Buying
 *   credits, ordering, renewing, cancelling, adding a project.
 * - `'any-state'` - the route carries `refuseWhenImpersonating` and nothing
 *   else, so **a suspended account may still use it**. Settings, the login
 *   email, the contact forms, and editing an existing project.
 *
 * **Getting this wrong puts a false sentence on the screen**, and in one case a
 * harmful one: `POST /api/contact-messages` is deliberately open to a suspended
 * agency (`requireAgencyState('active', 'suspended')`) so they can ask why they
 * were suspended. A page that greyed that form out and said "you cannot send us
 * a message while your account is suspended" would close the one door the
 * ruling left open.
 */
export type ServerGuard = 'active-only' | 'any-state';

/**
 * Can this page write at all?
 *
 * True when the agency is suspended **or** an operator is viewing. Both mean
 * "the server will refuse", and every control that would write asks one
 * question rather than two.
 *
 * `guard` says which refusals apply to the route this control calls; it
 * defaults to the stricter reading, so an existing call site keeps its
 * behaviour and a new one has to think about the answer only when suspension
 * genuinely does not block it.
 */
export function useWritesRefused(guard: ServerGuard = 'active-only'): {
  refused: boolean;
  reason: 'suspended' | 'viewing' | null;
} {
  const suspended = useAgencySuspended() && guard === 'active-only';
  const viewing = useViewingAsAgency();

  /*
   * **Viewing wins when both are true**, and viewing a suspended agency is the
   * common case rather than an edge one: it is the state an operator is most
   * often trying to understand. "You cannot buy credits while your account is
   * suspended" addressed to an operator is the wrong sentence to the wrong
   * person.
   */
  if (viewing) return { refused: true, reason: 'viewing' };
  if (suspended) return { refused: true, reason: 'suspended' };
  return { refused: false, reason: null };
}

/** The right line for whichever reason applies, or nothing. */
export function RefusalNotice({
  action,
  guard = 'active-only',
}: {
  action: SuspendedAction;
  guard?: ServerGuard;
}): React.JSX.Element | null {
  const { reason } = useWritesRefused(guard);

  if (reason === 'viewing') return <ViewOnlyNotice />;
  if (reason === 'suspended') return <SuspendedNotice action={action} />;
  return null;
}
